import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import type { AnalyticsQueueMetricsResponse, QueueConfigSummary } from "@situate/contracts/runtime";
import {
  analyticsQueueMetricsResponseSchema,
  queueConfigsListResponseSchema,
} from "@situate/contracts/runtime-zod";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";
import type { LiveViewQueue } from "@/features/deployment-dashboard/live-view/model/queue";
import { formatAnalyticsPeriodLabel } from "@/features/deployment-dashboard/shared/analytics-query-range";
import type { QueueWaitTrendData } from "@/features/deployment-dashboard/widgets/charts/queue-wait-trend/types";
import {
  isNearEmptyQueueWait,
  toChartQueueWaitMinutes,
} from "@/features/deployment-dashboard/shared/queue/queue-wait";

const QUEUE_CONFIGS_PATH = (areaId: string) => `/v1/areas/${encodeURIComponent(areaId)}/queues`;
const QUEUE_METRICS_PATH = "/v1/analytics/queues";

function outputGranularity(startDate: Date, endDate: Date): "1m" | "5m" | "15m" | "1h" {
  const hours = (endDate.getTime() - startDate.getTime()) / 3_600_000;
  if (hours <= 2) return "1m";
  if (hours <= 12) return "5m";
  if (hours <= 72) return "15m";
  return "1h";
}

function toQueueView(
  queue: Pick<QueueConfigSummary, "queue_id" | "queue_display_name" | "queue_type" | "area_id">
): LiveViewQueue {
  return {
    queue_id: queue.queue_id,
    queue_display_name: queue.queue_display_name,
    queue_type: queue.queue_type,
    area_id: queue.area_id,
    peopleInQueue: null,
    estimatedWaitMinutes: null,
    lastUpdatedAt: "",
    status: null,
    peopleDataStatus: "unavailable",
    waitDataStatus: "unavailable",
    historicalData: true,
  };
}

export function mapQueueMetricsResponse(
  response: AnalyticsQueueMetricsResponse,
  granularity: "1m" | "5m" | "15m" | "1h"
): QueueWaitTrendData[] {
  const periodGranularity = granularity === "1h" ? "hourly" : "60s";

  return response.queues.map((queue) => {
    return {
      queue: toQueueView({
        queue_id: queue.queue_id,
        area_id: queue.area_id,
        queue_display_name: queue.queue_display_name,
        queue_type: queue.queue_type,
      }),
      thresholds: queue.thresholds,
      points: queue.data_points.map((point) => {
        const nearEmptyWait = isNearEmptyQueueWait({
          value: point.estimated_queue_wait_time_minutes,
          dataStatus: point.data_status,
          qualityReasons: point.quality_reasons,
        });
        return {
          period: formatAnalyticsPeriodLabel(point.window_start, periodGranularity),
          timestamp: point.window_start,
          averageWaitMinutes: toChartQueueWaitMinutes(
            point.estimated_queue_wait_time_minutes,
            nearEmptyWait
          ),
          peakWaitMinutes: null,
          waitDataStatus: point.data_status,
          waitQualityReasons: point.quality_reasons,
          nearEmptyWait,
        };
      }),
    };
  });
}

export function useQueueMetrics(
  deploymentId: string,
  areaIds: readonly string[],
  startDate: Date,
  endDate: Date,
  loadEnabled = true
) {
  const runtime = useRuntimeHttpClient();
  const client = runtime.client;
  const hasConfigGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "queue_configs_list"
  );
  const hasMetricsGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "analytics_queue_metrics"
  );
  const enabled = loadEnabled && client !== null && runtime.deploymentId === deploymentId;

  const configQueries = useQueries({
    queries: areaIds.map((areaId) => ({
      queryKey: ["queue-configs", deploymentId, areaId],
      queryFn: async () => {
        if (client === null) throw new Error("Runtime client is unavailable");
        const response = await client.get<unknown>(QUEUE_CONFIGS_PATH(areaId));
        return queueConfigsListResponseSchema.parse(response);
      },
      enabled: enabled && hasConfigGrant,
    })),
  });

  const queueConfigs = useMemo(
    () =>
      configQueries.flatMap((query) => query.data?.queues.filter((queue) => queue.enabled) ?? []),
    [configQueries]
  );
  const queueIds = useMemo(
    () => [...new Set(queueConfigs.map((queue) => queue.queue_id))].sort(),
    [queueConfigs]
  );
  const validDates = Number.isFinite(startDate.getTime()) && Number.isFinite(endDate.getTime());
  const granularity = outputGranularity(startDate, endDate);
  const query = useQuery({
    queryKey: [
      "analytics-queue-metrics",
      deploymentId,
      queueIds,
      startDate.toISOString(),
      endDate.toISOString(),
    ],
    queryFn: async (): Promise<AnalyticsQueueMetricsResponse> => {
      if (client === null) throw new Error("Runtime client is unavailable");
      const response = await client.post<unknown>(QUEUE_METRICS_PATH, {
        queue_ids: queueIds,
        time_range: { start: startDate.toISOString(), end: endDate.toISOString() },
        granularity,
      });
      return analyticsQueueMetricsResponseSchema.parse(response);
    },
    enabled: enabled && hasMetricsGrant && queueIds.length > 0 && validDates && endDate > startDate,
    placeholderData: (previousData) => previousData,
  });

  const configError = configQueries.find((result) => result.error instanceof Error)?.error;
  const data = useMemo(
    () => (query.data === undefined ? [] : mapQueueMetricsResponse(query.data, granularity)),
    [granularity, query.data]
  );
  return {
    data,
    isLoading:
      configQueries.some((result) => result.isLoading) || (query.isLoading && query.data == null),
    error:
      configError instanceof Error
        ? configError
        : query.error instanceof Error
          ? query.error
          : null,
  };
}
